import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn, combineIssues, severityStyle } from '../../../utils';
import { Icon } from '../../../components/icons/Icon';
import type { ValidationIssue } from '../../../types';

const wrapperVariants = cva('items-center gap-sm ', {
  variants: {
    display: {
      inline: 'inline-flex',
      block: 'flex'
    }
  },
  defaultVariants: {
    display: 'inline'
  }
});

const buttonVariants = cva(
  'p-xs text-sm text-left items-center group transition-colors border-solid',
  {
    variants: {
      display: {
        inline: 'flex',
        block: 'flex flex-1 min-w-0'
      },
      disabled: {
        true: 'opacity-60 cursor-not-allowed',
        false:
          'cursor-pointer hover:bg-muted/50 hover:border-info-border hover:border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group'
      },
      bordered: {
        true: 'border-border-base border',
        false: 'border border-transparent'
      },
      alwaysActive: {
        true: 'bg-background-input border border-border-input rounded-md min-h-10 px-sm',
        false: 'bg-transparent'
      }
    },
    defaultVariants: {
      display: 'inline',
      disabled: false,
      bordered: true,
      alwaysActive: false
    }
  }
);

export interface EditableFieldDisplayProps<TValue> {
  value: TValue;
  formatter?: (value: TValue, errors: ValidationIssue[]) => React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  display?: 'inline' | 'block';
  errors: ValidationIssue[];
  onActivate: () => void;
  /**
   * Optional focus handler — fires when the button element receives focus
   * (keyboard or programmatic). Used by `EditableField` to wire `openOnFocus`.
   */
  onFocus?: () => void;
  /** Ref to the focusable button element, used for programmatic focus restoration. */
  buttonRef?: React.Ref<HTMLDivElement>;
  className?: string;
  buttonClassName?: string;
  showErrors?: boolean;
  /**
   * When false, takes the button out of the tab order (`tabIndex=-1`). Use
   * when a parent wrapper owns keyboard focus and delegates activation to
   * this field. Defaults to true.
   */
  tabbable?: boolean;
  bordered?: boolean;
  /** Apply input-like styling (white background, visible border, standard input height) in display mode. */
  alwaysActive?: boolean;
  /** `ArrowDown` / `ArrowUp` activate the field like `Enter`. */
  openOnArrowKeys?: boolean;
}

const isEmpty = (val: unknown): boolean => val === null || val === undefined || val === '';

export function EditableFieldDisplay<TValue>({
  value,
  formatter,
  placeholder = '—',
  disabled = false,
  display = 'inline',
  errors,
  onActivate,
  onFocus,
  buttonRef,
  className,
  buttonClassName,
  showErrors = true,
  tabbable = true,
  bordered = true,
  alwaysActive = false,
  openOnArrowKeys = false
}: EditableFieldDisplayProps<TValue>) {
  const combinedIssue = combineIssues(errors);
  const severityClasses = severityStyle(combinedIssue?.severity);

  const classes = cn(className, wrapperVariants({ display }), 'block');

  return (
    <div className={classes}>
      <div
        ref={buttonRef}
        role='button'
        tabIndex={disabled ? undefined : tabbable ? 0 : -1}
        aria-disabled={disabled}
        onClick={() => !disabled && onActivate()}
        onFocus={() => {
          if (disabled) return;
          onFocus?.();
        }}
        onKeyDown={event => {
          if (disabled) return;
          const arrow = event.key === 'ArrowDown' || event.key === 'ArrowUp';
          if (event.key === 'Enter' || event.key === ' ' || (openOnArrowKeys && arrow)) {
            event.preventDefault();
            onActivate();
          }
        }}
        className={cn(
          buttonVariants({
            display,
            disabled,
            bordered: alwaysActive ? false : bordered,
            alwaysActive
          }),
          severityClasses?.border,
          severityClasses?.bgColor,
          buttonClassName
        )}
      >
        {isEmpty(value) && placeholder ? (
          <span className={cn('text-foreground-muted', display === 'block' ? 'flex-1' : 'flex-1')}>
            {placeholder}
          </span>
        ) : (
          <span className={display === 'block' ? 'flex-1 min-w-0' : 'flex-1'}>
            {formatter ? formatter(value, errors) : String(value)}
          </span>
        )}
        {!disabled && !combinedIssue && showErrors && (
          <Icon
            name='pen'
            className='h-3 w-3 opacity-0 group-hover:opacity-100 text-foreground-muted transition-opacity shrink-0'
          />
        )}
      </div>
      {combinedIssue && severityClasses && showErrors && (
        <div className={cn('shrink-0', severityClasses.iconColor)}>
          {errors.map(error => (
            <div key={error.message}>{error.message}</div>
          ))}
        </div>
      )}
    </div>
  );
}

EditableFieldDisplay.displayName = 'EditableFieldDisplay';
